/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 */

import {
  humanizeChipValue,
  translatePropertyChipLabel,
} from '@/shared/utils/property-chip-label';

export const PROPERTY_CATEGORIES = [
  'Casa', 'Apartamento', 'Terreno', 'Prédio Comercial', 'Sala Comercial', 'Galpão',
  'Kitnet', 'Studio', 'Flat', 'Loft', 'Cobertura', 'Chácara', 'Casa Comercial',
  'Ponto Comercial', 'Garagem', 'Fazenda', 'Sítio', 'Pousada', 'Academia', 'Edifício',
] as const;

export const CATEGORY_LABELS: Record<string, string> = {
  'Prédio Comercial': 'Prédio Comercial',
  'Galpão': 'Galpão',
  'Casa Comercial': 'Casa Comercial',
  'Ponto Comercial': 'Ponto Comercial',
  'Chácara': 'Chácara',
  'Sítio': 'Sítio',
  'Predio Comercial': 'Prédio Comercial',
  Galpao: 'Galpão',
  Casa_Comercial: 'Casa Comercial',
  Ponto_Comercial: 'Ponto Comercial',
  Chacara: 'Chácara',
  Sitio: 'Sítio',
  Kitnet: 'Quitinete',
  Studio: 'Estúdio',
  Flat: 'Apart-hotel',
  Loft: 'Loft residencial',
};

export const RESIDENTIAL_CATEGORIES = [
  'Casa', 'Apartamento', 'Kitnet', 'Studio', 'Flat', 'Loft', 'Cobertura', 'Chácara',
] as const;

export const APARTMENT_CATEGORIES = ['Apartamento', 'Cobertura'] as const;

export const RURAL_CATEGORIES = ['Chácara', 'Fazenda', 'Sítio'] as const;

export const LAND_CATEGORIES = ['Terreno'] as const;

export const NO_CONSTRUCTED_AREA_CATEGORIES = ['Terreno', 'Garagem'] as const;

/**
 * Stored in pt-BR, as the backend keeps them. Temporada is short-term letting
 * and never a kind of Aluguel; Leilão only records the auction rounds.
 */
export const TRANSACTION_TYPES = ['Venda', 'Aluguel', 'Permuta', 'Temporada', 'Leilão'] as const;

/** Transaction a short-term (Temporada) price marks on its own. */
export const SHORT_TERM_TRANSACTION = 'Temporada';

/** Transaction an auction (Leilão) price marks on its own. */
export const AUCTION_TRANSACTION = 'Leilão';

/** `characteristics.condition` keys (labels live in i18n only). */
export const PROPERTY_CONDITIONS = [
  'new', 'like_new', 'used', 'renovated', 'needs_renovation',
  'under_construction', 'pre_launch', 'launch', 'ready',
] as const;

/** `characteristics.occupancy` keys. */
export const OCCUPANCIES = ['vacant', 'owner', 'tenant', 'developer'] as const;

/** `characteristics.finish_standard` keys. */
export const FINISH_STANDARDS = ['low', 'medium', 'medium_high', 'high', 'luxury'] as const;

/** `characteristics.location_quality` keys. */
export const LOCATION_QUALITIES = ['regular', 'good', 'great', 'prime'] as const;

/** Tri-state answers for the ownership flags: absent means "not informed". */
export const YES_NO_OPTIONS = ['yes', 'no'] as const;

// Amenity values are the backend's tokens (housien/utils/property_value_constants.py),
// never display text: the chip label comes from `schema.<group>.<token>` in i18n.
// Editor-era labels still stored on older listings are mapped back to these
// tokens on load (see legacy-amenity-labels.ts).
export const LEISURE_AREAS = [
  'Piscina', 'Quadra_poliesportiva', 'Academia', 'Playground', 'Salao_de_festas',
  'Churrasqueira', 'Sauna', 'Espaco_pet', 'Jardim', 'Espaco_gourmet',
  'Sacada', 'Varando_Gourmet', 'Salao_de_jogos', 'Brinquedoteca',
] as const;

export const FACILITIES = [
  'Elevador', 'Ar_condicionado', 'Aquecimento_central', 'Area_de_servico',
  'Lavanderia', 'Deposito', 'Bicicletario', 'Coworking',
  'Armarios_Planejados', 'Hidromassagem', 'Lareira', 'Mobiliada', 'Garagem',
] as const;

export const SECURITY_ITEMS = [
  'Seguranca_24h', 'Cameras_de_seguranca', 'Alarme', 'Cerca_eletrica',
  'Portaria', 'Guarita', 'Interfone', 'Circuito_de_seguranca',
] as const;

/** What the lot is served by; a previous system's "Detalhes básico". */
export const INFRASTRUCTURE_ITEMS = [
  'Água', 'Energia', 'Esgoto', 'Pavimentação', 'Hidrômetro individual',
  'Água quente', 'Murado', 'Gradeado',
] as const;

export const CONDO_AMENITIES = [
  'Piscina', 'Quadra_poliesportiva', 'Academia', 'Playground', 'Salao_de_festas',
  'Churrasqueira', 'Sauna', 'Espaco_pet', 'Elevador',
  'Espaco_gourmet', 'Salao_de_jogos', 'Brinquedoteca', 'Hidromassagem',
  'Sala_de_cinema', 'Quadra_de_tenis', 'Quadra_de_volei', 'Quadra_de_futebol',
  'Quadra_poliesportiva_de_areia', 'Campo_de_futebol',
] as const;

export const CONDO_SECURITY = [
  'Seguranca_24h', 'Cameras_de_seguranca', 'Controle_de_acesso', 'Ronda_noturna',
  'Cerca_eletrica', 'Guarita_blindada', 'Portaria', 'Guarita', 'Interfone',
  'Alarme', 'Circuito_de_seguranca',
] as const;

export const MANDATE_TYPES = ['exclusive', 'simple', 'unknown'] as const;

export const FURNISHED_OPTIONS = ['furnished', 'semi_furnished', 'unfurnished'] as const;

export const FACING_OPTIONS = ['north', 'south', 'east', 'west', 'northeast', 'northwest', 'southeast', 'southwest'] as const;

export const PARKING_TYPE_OPTIONS = ['covered', 'uncovered', 'underground', 'automatic'] as const;

export const RENTAL_PAYMENT_FREQUENCY_OPTIONS = ['Mensal', 'Bimestral', 'Trimestral', 'Semestral', 'Anual'] as const;

export const RENTAL_INCLUDED_EXPENSES_OPTIONS = ['Agua', 'Luz', 'Gas', 'IPTU', 'Internet', 'TV_a_cabo'] as const;

export const ADMINISTRATION_TYPE_OPTIONS = ['Imobiliaria', 'Particular'] as const;

export const CONDO_PAID_BY_OPTIONS = ['Inquilino', 'Imobiliaria'] as const;

export const CONDO_INCLUDED_EXPENSES_OPTIONS = ['water', 'gas', 'internet', 'cleaning', 'maintenance', 'security'] as const;

/** Hard caps for integer and string property-form fields. */
export const PROPERTY_FIELD_LIMITS = {
  yearBuilt: { min: 1500, maxOffsetYears: 5, maxDigits: 4 },
  totalRooms: { min: 0, max: 99, maxDigits: 2 },
  floor: { min: 0, max: 200, maxDigits: 3 },
  quantity: { min: 0, max: 20, maxDigits: 2 },
  leaseTermMonths: { min: 0, max: 600, maxDigits: 3 },
  registrationNumber: { maxLength: 30 },
  registryOffice: { maxLength: 120 },
  inscriptionNumber: { maxLength: 30 },
  propertyTitle: { maxLength: 120 },
  propertyCode: { maxLength: 20 },
  description: { maxLength: 5000 },
  street: { maxLength: 200 },
  number: { maxLength: 10 },
  complement: { maxLength: 100 },
  neighborhood: { maxLength: 100 },
  city: { maxLength: 100 },
  money: { maxDigits: 12 },
  area: { maxDigits: 7 },
} as const;

/**
 * Latest year accepted for year_built (current calendar year plus a small buffer
 * for properties still under construction).
 *
 * @returns {number} The inclusive maximum construction year.
 */
export function maxYearBuilt(): number {
  return new Date().getFullYear() + PROPERTY_FIELD_LIMITS.yearBuilt.maxOffsetYears;
}

type TFunction = (key: string, opts?: { defaultValue?: string }) => string;

export type SchemaItemGroup =
  | 'categories'
  | 'transactionTypes'
  | 'leisureAreas'
  | 'facilities'
  | 'securityItems'
  | 'condoAmenities'
  | 'condoSecurity';

/** @deprecated Prefer humanizeChipValue from shared utils. */
export const humanizeSchemaValue = humanizeChipValue;

/**
 * Translates a schema item using the provided translator.
 */
export function translateSchemaItem(
  t: TFunction,
  group: SchemaItemGroup,
  value: string,
): string {
  const key = `schema.${group}.${value}`;
  const label = t(key, { defaultValue: '' });
  if (label && label !== key) return label;
  return humanizeChipValue(value);
}

/**
 * Translates a chip value via schema.* then form.options.* lookups.
 */
export function translateKnownSchemaItem(t: TFunction, value: string): string {
  return translatePropertyChipLabel(t, value);
}
